import Swiper from 'swiper';
import { refs, booleanDataset, domReady } from '../modules/utils.js';
import { test as testMedia } from '../modules/match-media.js';

class LightboxOverlay extends HTMLElement {
  static get observedAttributes() {
    return ['data-display', 'data-type', 'data-side', 'data-index', 'data-index-total'];
  }

  constructor () {
    super();
    this.cursorListener = this.#cursorListener.bind(this);
    this.keydownListener = this.#keydownListener.bind(this);
    this.touchStartY = 0;
    this.touchEndY = 0;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-display') {
      const display = newValue === '' ? true : false;
      const unscrollable = display ? true : false;
      booleanDataset(document.documentElement, 'unscrollable', unscrollable);

      // Add/remove keyboard listener based on display state
      if (display) {
        document.addEventListener('keydown', this.keydownListener);
      } else {
        document.removeEventListener('keydown', this.keydownListener);
      }

      if (!display) {
        setTimeout(() => this.#clear(), 300);
      }
    }

    if (name === 'data-type') {
      if (newValue === 'gallery') {
        this.#handleCursor();
      }
    }

    if (name === 'data-side') {
      this.refs.cursor.textContent = newValue === 'left' ? 'PREV' : 'NEXT';
    }

    if (name === 'data-index') {
      const index = parseInt(newValue);
      this.refs.indexList.style.top = `calc(var(--line-height-default) * -${index})`;
    }

    if (name === 'data-index-total') {
      this.refs.indexTotal.textContent = `${newValue}`.padStart(2, '0');
    }
  }

  async connectedCallback() {
    await domReady();
    this.refs = refs(this);
    this.#listeners();

    if (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0)) {
      booleanDataset(this, 'isTouch', true);
    }
  }

  #listeners() {
    document.addEventListener('videobox:play', ({ detail }) => {
      this.dataset.type = 'video';
      booleanDataset(this, 'display', true);
      this.#renderVideoSlide(detail);
    });

    document.addEventListener('archivefeed:gallery', ({ detail }) => {
      this.dataset.type = 'gallery';
      booleanDataset(this, 'display', true);

      const title = detail.items[0]?.name || '';
      if (title) {
        this.refs.title.textContent = title;
      }

      this.dataset.index = detail.index;
      this.dataset.indexTotal = detail.items.length;

      for (let i = 0; i < detail.items.length; i++) {
        const li = `<li>${(i + 1).toString().padStart(2, '0')}</li>`;
        this.refs.indexList.innerHTML += li;
      }

      this.#renderGallery(detail);
      this.#initGallery(detail.index);
    });

    this.refs.close.addEventListener('click', () => {
      booleanDataset(this, 'display', false);
    });

    this.refs.close.addEventListener('mouseenter', () => booleanDataset(this.refs.cursor, 'hide', true));
    this.refs.close.addEventListener('mouseleave', () => booleanDataset(this.refs.cursor, 'hide', false));

    this.refs.prev.addEventListener('click', () => {
      if (this.slider) {
        this.slider.slidePrev();
      }
    });

    this.refs.next.addEventListener('click', () => {
      if (this.slider) {
        this.slider.slideNext();
      }
    });

    // Touch gestures for closing lightbox
    this.addEventListener('touchstart', (e) => {
      this.touchStartY = e.changedTouches[0].screenY;
    });

    this.addEventListener('touchend', (e) => {
      this.touchEndY = e.changedTouches[0].screenY;
      const touchDiff = this.touchStartY - this.touchEndY;

      // Swipe down to close (threshold: 100px)
      if (touchDiff < -100) {
        booleanDataset(this, 'display', false);
      }
    });
  }

  #initGallery(initialSlide) {
    this.slider = new Swiper(
      this.querySelector('.swiper'),
      {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        initialSlide
      }
    );

    this.slider.on('slideChange', () => {
      this.dataset.index = this.slider.realIndex;
    });
  }

  #clear() {
    delete this.dataset.type;
    delete this.dataset.side;
    this.refs.content.innerHTML = '';
    this.refs.title.textContent = '';
    this.refs.indexList.innerHTML = '';
    booleanDataset(this.refs.cursor, 'hide', false);
    document.removeEventListener('mousemove', this.cursorListener);
  }

  #keydownListener(e) {
    // Escape key closes lightbox
    if (e.key === 'Escape') {
      booleanDataset(this, 'display', false);
      return;
    }

    // Arrow key navigation for gallery
    if (this.dataset.type === 'gallery' && this.slider) {
      if (e.key === 'ArrowLeft') {
        this.slider.slidePrev();
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        this.slider.slideNext();
        e.preventDefault();
      }
    }
  }

  #renderGallery(detail) {
    this.slider = null;
    this.refs.content.innerHTML = '<div class="swiper"><div class="swiper-wrapper"></div></div>';
    const wrapper = this.refs.content.querySelector('.swiper-wrapper');
    let hasVideos = false;

    detail.items.forEach((item) => {
      let slide = '';
      if (item.type === 'image') {
        slide = `
          <div class="swiper-slide">
            <picture>
              <source srcset="${item.imageSmallUrl}" media="(max-width: 320px)" />
              <source srcset="${item.imageMediumUrl}" media="(min-width: 401px) and (max-width: 640px)" />
              <source srcset="${item.imageLargeUrl}" media="(min-width: 641px) and (max-width: 960px)" />
              <img src="${item.imageWideUrl}" alt="${item.name}" loading="lazy" />
            </picture>
          </div>`;
      } else if (item.type === 'video') {
        hasVideos = true;
        slide = `
          <div class="swiper-slide">
            <video src="${item.videoUrl}#t=0.001"" controls controlsList="nodownload"></video>
          </div>`;
      }
      wrapper.innerHTML += slide;
    });

    if (hasVideos) {
      const videos = wrapper.querySelectorAll('video');
      videos.forEach((video) => {
        video.addEventListener('mouseenter', () => booleanDataset(this.refs.cursor, 'hide', true));
        video.addEventListener('mouseleave', () => booleanDataset(this.refs.cursor, 'hide', false));
      });
    }
  }

  #renderVideoSlide(detail) {
    const videoEl = document.createElement('video');
    videoEl.src = detail.src;
    videoEl.controls = true;
    videoEl.autoplay = true;
    videoEl.controlsList = 'nodownload';

    this.refs.content.innerHTML = '';
    this.refs.content.appendChild(videoEl);
    this.refs.title.textContent = detail.title || '';
  }

  #handleCursor() {
    if (!testMedia('small')) {
      document.addEventListener('mousemove', this.cursorListener);
    }
  }

  #cursorListener(e) {
    this.refs.cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    // detect if the cursor is on the left or right side of the screen
    const cursorX = e.clientX;
    const windowWidth = window.innerWidth;
    const side = cursorX > windowWidth / 2 ? 'right' : 'left';

    if (side != this.dataset.side) {
      this.dataset.side = side;
    }
  }
}

customElements.define('lightbox-overlay', LightboxOverlay);
